import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, LayoutTemplate, Timer, ShieldCheck, ChevronDown } from "lucide-react";
import OfferMockup from "./OfferMockup";

const TRUST = [
  { icon: LayoutTemplate, label: "Complete Website", id: "complete" },
  { icon: Timer, label: "7-Day Delivery", id: "delivery" },
  { icon: ShieldCheck, label: "Money-Back Guarantee", id: "guarantee" },
];

const rise = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

const scrollToPricing = (e) => {
  const el = document.getElementById("pricing");
  if (!el) return;
  e.preventDefault();
  el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function OfferHero() {
  return (
    <section className="relative overflow-hidden bg-[#050505] noise" data-testid="section-hero">
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full bg-[#FF6A00]/10 blur-[160px] pointer-events-none" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-8 lg:pt-14 pb-14 lg:pb-20 grid lg:grid-cols-12 gap-12 lg:gap-10 relative items-center">
        <div className="lg:col-span-6">
          <motion.div {...rise(0)} data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-white/75 border border-white/10 bg-white/[0.03] rounded-full px-3 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF6A00] animate-pulse" />
            Your business deserves a better website
          </motion.div>

          <motion.h1 {...rise(0.06)} data-testid="hero-headline"
            className="mt-6 font-display font-bold tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.98]">
            Your Complete Business Website.{" "}
            <span className="whitespace-nowrap">
              Just <span className="relative inline-block text-[#FF6A00]" data-testid="hero-price">
                $199.
                <motion.span aria-hidden initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: 0.7, ease: "easeOut" }}
                  className="absolute left-0 right-1 -bottom-1 h-[6px] rounded-full bg-[#FF6A00]/35 origin-left" />
              </span>
            </span>
          </motion.h1>

          <motion.p {...rise(0.14)} data-testid="hero-subhead" className="mt-6 text-base lg:text-lg text-white/70 max-w-xl leading-relaxed">
            Launch your professional business website in just 7 days, with a money-back guarantee for added peace of mind.
          </motion.p>

          <motion.ul {...rise(0.22)} className="mt-7 flex flex-wrap gap-2 sm:gap-3" data-testid="hero-trust-badges">
            {TRUST.map(({ icon: Icon, label, id }) => (
              <li key={id} data-testid={`hero-trust-${id}`}
                className="inline-flex items-center gap-2 text-sm px-3.5 py-2 rounded-full border border-white/10 bg-white/[0.04] text-white/90">
                <span className="w-6 h-6 rounded-full bg-[#FF6A00]/15 flex items-center justify-center">
                  <Icon className="w-3.5 h-3.5 text-[#FF6A00]" />
                </span>
                {label}
              </li>
            ))}
          </motion.ul>

          <motion.div {...rise(0.3)} className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link to="/contact?package=starter-site" className="btn-primary justify-center group" data-testid="hero-cta-primary">
              Get Started Now <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <a href="#pricing" onClick={scrollToPricing} className="btn-ghost justify-center group" data-testid="hero-cta-secondary">
              Explore the $199 Package <ChevronDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
            </a>
          </motion.div>

          <motion.p {...rise(0.38)} className="mt-5 text-xs text-white/45 max-w-md" data-testid="hero-guarantee-note">
            Money-back guarantee: full refund if you&apos;re not happy with your website before it goes live.
          </motion.p>
        </div>

        <div className="lg:col-span-6 relative">
          <OfferMockup />
        </div>
      </div>
    </section>
  );
}
